import CoreAnalytics from './coreAnalytics';

/** @typedef {import('./analyticsTypes').SessionActivityEventPayload} */
/** @typedef {import('./analyticsTypes').SessionActivityWithAppointmentEventPayload} */
/** @typedef {import('./analyticsTypes').SessionActivityReopenedEventPayload} */

/**
 * Analytics service related to session functionality.
 */
class SessionAnalytics extends CoreAnalytics {
  EVENT_NAMES = {
    ACTIVITY_REOPENED: 'session_activity_reopened',
    ACTIVITY_NOTES_EDITED_AFTER_REOPENED: 'session_activity_notes_edited_after_reopened',
    ACTIVITY_COMPLETED: 'session_activity_completed',
    ACTIVITY_LINKED_TO_APPOINTMENT_MANUALLY: 'session_activity_linked_to_appointment_manually',
    STAFF_ACTIVITY_CREATED_FROM_PAST_APPOINTMENT: 'staff_session_activity_created_from_past_appointment',
    ACTIVITY_AUDIT_TRAIL_OPENED: 'session_activity_audit_trail_opened',
  };

  /**
   * Track when a session activity is reopened.
   * @param {SessionActivityReopenedEventPayload} payload
   */
  trackActivityReopened(payload) {
    const {
      sessionActivityId, sessionActivityType, wasAuditedBeforeSigning, sessionActivityAuditId,
    } = payload ?? {};

    if (!sessionActivityId) {
      return;
    }

    this.track(this.EVENT_NAMES.ACTIVITY_REOPENED, {
      sessionActivityId,
      sessionActivityType,
      wasAuditedBeforeSigning,
      sessionActivityAuditId,
      eventVersion: 3,
    });
  }

  /**
   * Track when a session activity is completed.
   *
   * @param {SessionActivityEventPayload} payload
   */
  trackActivityCompleted(payload) {
    const { sessionActivityId, sessionActivityType } = payload ?? {};

    if (!sessionActivityId) {
      return;
    }

    this.track(this.EVENT_NAMES.ACTIVITY_COMPLETED, {
      sessionActivityId,
      sessionActivityType,
      eventVersion: 1,
    });
  }

  /**
   * Track when session activity notes are updated after the activity was reopened.
   *
   * @param {SessionActivityEventPayload} payload
   */
  trackActivityNotesEditedAfterReopened(payload) {
    const { sessionActivityId, sessionActivityType } = payload ?? {};

    if (!sessionActivityId) {
      return;
    }

    this.track(this.EVENT_NAMES.ACTIVITY_NOTES_EDITED_AFTER_REOPENED, {
      sessionActivityId,
      sessionActivityType,
      eventVersion: 1,
    });
  }

  /**
   * Track when a session activity is linked to an appointment by hand.
   *
   * @param {SessionActivityWithAppointmentEventPayload} payload
   */
  trackActivityLinkedToAppointmentManually(payload) {
    const { sessionActivityId, sessionActivityType, appointmentId } = payload ?? {};

    if (!sessionActivityId || !appointmentId) {
      return;
    }

    this.track(this.EVENT_NAMES.ACTIVITY_LINKED_TO_APPOINTMENT_MANUALLY, {
      sessionActivityId,
      sessionActivityType,
      appointmentId,
      eventVersion: 1,
    });
  }

  /**
   * Track when session activity is created from a past appointment.
   *
   * @param {SessionActivityWithAppointmentEventPayload} payload
   */
  trackActivityCreatedFromPastAppointment(payload) {
    const { sessionActivityId, sessionActivityType, appointmentId } = payload ?? {};

    if (!sessionActivityId || !appointmentId) {
      return;
    }

    this.track(this.EVENT_NAMES.STAFF_ACTIVITY_CREATED_FROM_PAST_APPOINTMENT, {
      sessionActivityId,
      sessionActivityType,
      appointmentId,
      eventVersion: 1,
    });
  }

  /**
   * Track when the audit trail of a session activity is opened, from any screen.
   *
   * @param {{ sessionActivityId: string }} payload
   */
  trackActivityAuditTrailOpened(payload) {
    const { sessionActivityId } = payload ?? {};

    if (!sessionActivityId) {
      return;
    }

    this.track(this.EVENT_NAMES.ACTIVITY_AUDIT_TRAIL_OPENED, {
      sessionActivityId,
      eventVersion: 1,
    });
  }
}

export default new SessionAnalytics();
